// src/components/pages/backtest/hooks/useBacktestRunner.js

import { useMemo, useCallback, useEffect } from "react";
import { toast } from 'react-toastify';
import styles from '../style/BacktestPage.module.css'; 

// =========================================================
// 💡 useBacktestRunner Hook 정의
// =========================================================
export function useBacktestRunner(setup, detailRef, t, navigate) {

    // 1. 최종 벤치마크 파라미터 계산 (useMemo)
    const { finalBenchmarkParam, benchmarkHasValidInput } = useMemo(() => { 
        if (setup.benchmarkInputMode === 'portfolio') {
            const validBenchmarkRows = setup.benchmarkRows.filter(row => row.ticker && row.weight && parseFloat(row.weight) > 0);
            
            const sum = validBenchmarkRows.reduce((acc, row) => acc + parseFloat(row.weight), 0);
            const isValidWeightSum = sum > 99.9 && sum < 100.1;

            if (!isValidWeightSum) {
                return { finalBenchmarkParam: undefined, benchmarkHasValidInput: false };
            }

            // Ticker:Weight(0.5) 형식의 문자열로 변환 (예: "AAPL:0.40,MSFT:0.30")
            const finalParam = validBenchmarkRows.map(h => 
                `${h.ticker.toUpperCase()}:${(parseFloat(h.weight) / 100).toFixed(4)}`
            ).join(',');
            
            return { finalBenchmarkParam: finalParam, benchmarkHasValidInput: true };

        } else { // 'ticker' 모드
            const ticker = setup.benchmark.toUpperCase();
            const benchmarkHasValidInput = ticker.length > 0;
            return { finalBenchmarkParam: ticker, benchmarkHasValidInput };
        }
    }, [setup.benchmarkInputMode, setup.benchmarkRows, setup.benchmark]); 


    // 2. 백테스트 실행 (핵심 로직)
    const handleRunBacktest = useCallback(async () => {
        setup.setIsBacktestReady(false);
        setup.setMessage("");

        // ---------------------------------------------------------
        // [수정 시작] 1. 실행할 데이터 결정 (입력값 vs 예시값)
        // ---------------------------------------------------------
        let targetRows = setup.validRows;

        // 만약 입력된 종목이 하나도 없다면 -> 예시 데이터를 로드하고 즉시 실행 준비
        if (targetRows.length === 0) {
            const exampleRows = [
                { ticker: "AAPL.US", weight: "40" },
                { ticker: "MSFT.US", weight: "30" },
                { ticker: "GOOG.US", weight: "30" }
            ];

            // (1) UI 상태 업데이트: 화면에 종목이 채워지도록 함 (비동기)
            setup.setRows(exampleRows);
            toast.info(t('loadExampleDisclaimer') || "예시 포트폴리오로 백테스트를 실행합니다.");

            // (2) 로직용 변수 업데이트: 즉시 실행을 위해 로컬 변수에 할당
            targetRows = exampleRows;
        }

        // 편의를 위해 targetRows를 기반으로 파생 데이터 미리 계산
        const targetStocksArray = targetRows.map(r => r.ticker);
        const targetTotalWeight = targetRows.reduce((acc, row) => acc + Number(row.weight || 0), 0);
        const targetWeightsString = targetRows.map(row => row.weight).join(',');
        const targetManualWeights = targetRows.reduce((acc, row) => {
            acc[row.ticker.toUpperCase()] = row.weight;
            return acc;
        }, {});
        // ---------------------------------------------------------
        // [수정 끝]
        // ---------------------------------------------------------


        // 2. 비중 유효성 검사 (targetTotalWeight 사용)
        if (Math.abs(targetTotalWeight - 100) > 0.1) {
            setup.setMessage(t("backtest.weightSumMustBe100", { totalWeight: targetTotalWeight.toFixed(2) }));
            return;
        }

        // 3. 기간 유효성 검사
        if (setup.isCustomPeriod && (!setup.startDate || !setup.endDate || new Date(setup.startDate) >= new Date(setup.endDate))) {
            toast.error(t("backtest.error.invalidCustomDate"));
            return;
        }

        // 4. 벤치마크 유효성 검사 (비동기)
        const isValid = await setup.validateBenchmark();

        if (!isValid) {
            const defaultMsg = setup.benchmarkInputMode === 'portfolio' ? 
                t("backtest.weightSumMustBe100") : t("backtest.benchmarkInvalidTicker");
            setup.setMessage(setup.benchmarkError || defaultMsg);
            return; 
        }
        
        // 5. URL 파라미터 구성 및 저장 (수정됨: target 변수들 사용)
        const newSearchParams = new URLSearchParams();
        
        // setup.stocksArray 대신 targetStocksArray 사용
        newSearchParams.set('tickers', targetStocksArray.join(','));
        // setup.validRows 매핑 대신 targetWeightsString 사용
        newSearchParams.set('weights', targetWeightsString); 
        
        newSearchParams.set('period', setup.backtestPeriod.toString()); 
        
        if (finalBenchmarkParam) {
            newSearchParams.set('benchmark', finalBenchmarkParam); 
        } else {
            newSearchParams.delete('benchmark');
        }

        if (setup.isCustomPeriod) {
            newSearchParams.set('start', setup.startDate);
            newSearchParams.set('end', setup.endDate);
        } else {
            newSearchParams.delete('start');
            newSearchParams.delete('end');
        }

        // URL 업데이트 (주소창에 반영 -> 페이지 리로드 없이 백테스트 트리거)
        navigate(`?${newSearchParams.toString()}`, { replace: true });
        
        // Local Storage 업데이트 (수정됨: target 변수들 사용)
        localStorage.setItem("myStocks", JSON.stringify(targetStocksArray));
        localStorage.setItem("optimizedPortfolioWeights", JSON.stringify(targetManualWeights));

        // 최종 실행 준비 완료
        setup.setIsBacktestReady(true);
    }, [setup, finalBenchmarkParam, t, navigate]);

    // 3. 백테스트 준비 완료 시 스크롤
    useEffect(() => {
        if (setup.isBacktestReady && detailRef.current) detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [setup.isBacktestReady, detailRef]);
    
    // 4. UI 헬퍼
    const getWeightSumColor = useCallback((sum) => {
        if (Math.abs(sum - 100) < 0.1) return styles.positive;
        if (sum > 100) return styles.negative;
        return styles.warning;
    }, []);
    
    const getMessageClass = useCallback((msg) => 
        msg.includes("100%여야 합니다") || msg.includes("is invalid") ? styles.errorMessage : styles.infoMessage
    , []);

    return {
        handleRunBacktest,
        finalBenchmarkParam,
        benchmarkHasValidInput,
        getWeightSumColor,
        getMessageClass,
    };
}